/**
 * Terra Anima (테라 아니마) - Production Main Entry Point
 */

import { EntityRegistry } from './core/EntityRegistry';
import { GameLoop } from './core/GameLoop';
import { CombatModule } from './modules/combat/CombatModule';
import { IsoRenderer } from './modules/view/IsoRenderer';
import { InputController } from './modules/view/InputController';
import { PartyHUD } from './modules/view/PartyHUD';
import { ActionBarHUD } from './modules/view/ActionBarHUD';
import { CombatLogHUD } from './modules/view/CombatLogHUD';
import { TargetFrameHUD } from './modules/view/TargetFrameHUD';
import { CharacterWindows } from './modules/view/CharacterWindows';
import { SoundManager } from './modules/audio/SoundManager';
import { GatheringModule } from './modules/gathering/GatheringModule';
import { EntityState } from './core/Types';
import { EventBus } from './core/EventBus';
import { getDungeonMonsterConfigs, getCemeteryMonsterConfigs } from './modules/world/DungeonMap';
import { getBiomeInfo } from './modules/world/BiomeSystem';
import { initCharacterSheet, initEntityInventory20, TERRA_ANIMA_RACES } from './modules/character/CharacterSystem';
import { computePersonalityProfile } from './modules/character/PersonalityQuiz';
import { getZoneData } from './modules/world/ZoneMapData';
import { screenToTile } from './modules/view/IsoMath';
import { StableRouteSystem, STABLE_NODES } from './modules/world/StableRouteSystem';
import { WorldClock } from './modules/world/WorldClock';
import { SplashScreen } from './modules/ui/SplashScreen';
import { MainMenu } from './modules/ui/MainMenu';
import { HudLayout, MinimapHUD, GroupHUD } from './modules/view/DaocHud';
import { Settings, displayKey } from './core/Settings';
import { PartyManager } from './modules/party/PartyManager';
import { NetClient, NetPartyTransport } from './core/NetClient';
import { RemotePlayers } from './modules/net/RemotePlayers';
import { PartyWindow } from './modules/party/PartyWindow';
import { GuildWindow } from './modules/guild/GuildWindow';
import { CharacterCreationFlow, CharacterCreationResult } from './modules/ui/CharacterCreationFlow';
import { AdminConsoleHUD } from './modules/admin/AdminConsoleHUD';
import { TouchControlsHUD } from './modules/view/TouchControlsHUD';
import { AccountSaveSystem, SavedCharacterSlot } from './core/AccountSaveSystem';
import { ActivityLog } from './core/ActivityLog';
import { RoyalShopModal } from './modules/ui/RoyalShopModal';
import { Currency } from './core/Currency';
import { VersionChecker, CLIENT_VERSION } from './core/VersionChecker';
import { ScreenNoticeHUD } from './modules/view/ScreenNoticeHUD';
import { moneySelectorModalInstance } from './modules/ui/MoneySelectorModal';
import { tradeModalInstance } from './modules/ui/TradeModal';
import { npcShopModalInstance } from './modules/ui/NpcShopModal';
import { enchanterModalInstance } from './modules/ui/EnchanterModal';
import { housingModeController } from './modules/ui/HousingModeController';
import { housingTopViewHUD } from './modules/ui/HousingTopViewHUD';
import { mailboxModalInstance } from './modules/ui/MailboxModal';
import { housingSystem } from './modules/world/HousingSystem';

// 0. Game State Machine: 'SPLASH' | 'MAIN_MENU' | 'CHAR_CREATE' | 'IN_WORLD'
let gameState: 'SPLASH' | 'MAIN_MENU' | 'CHAR_CREATE' | 'IN_WORLD' = 'SPLASH';
(window as any).__TERRA_GAME_STATE__ = () => gameState;

// 0. Initialize Miraero Games Splash Screen immediately on boot
const splashScreen = new SplashScreen();
const mainMenu = new MainMenu();
const charCreationFlow = new CharacterCreationFlow();

// Shared Royal Shop Modal (Used across Main Menu & In-World Gameplay)
const royalShopModal = new RoyalShopModal(() => {
  const p = registry?.getPlayer() || player;
  const user = mainMenu.getCurrentUser();
  return {
    gold: p?.gold ?? user?.gold ?? Currency.toCopper({ gold: 100 }),
    gems: user?.gems ?? 100,
  };
});

function setWorldUIActive(active: boolean): void {
  const displayVal = active ? 'block' : 'none';
  const flexVal = active ? 'flex' : 'none';

  if (canvas) canvas.style.display = displayVal;
  if (topNavEl) topNavEl.style.display = flexVal;
  
  // World HUD Elements
  const partyEl = document.getElementById('party-hud');
  if (partyEl) partyEl.style.display = flexVal;
  const targetEl = document.getElementById('target-frame-hud');
  if (targetEl) targetEl.style.display = active ? 'none' : 'none'; // TargetFrame manages its own visibility on target select
  const actionEl = document.getElementById('action-bar-hud');
  if (actionEl) actionEl.style.display = flexVal;
  const combatLogEl = document.getElementById('combat-log-hud');
  if (combatLogEl) combatLogEl.style.display = active ? 'flex' : 'none';
  const touchEl = document.getElementById('touch-controls-hud');
  if (touchEl) touchEl.style.display = active && window.innerWidth <= 768 ? 'block' : 'none';
  const stableEl = document.getElementById('stable-ride-hud');
  if (stableEl && !active) stableEl.style.display = 'none';
  // DAoC HUD 창들 (시계 / 그룹)
  for (const hid of ['hud-clock', 'group-hud']) {
    const he = document.getElementById(hid);
    if (he) he.style.display = active ? 'block' : 'none';
  }
}

function startCharacterCreationFlow(): void {
  charCreationFlow.start(
    (createdChar: CharacterCreationResult) => {
      console.log('✨ [CharacterCreation] Completed:', createdChar);
      
      // Update player entity
      player.name = createdChar.name;
      player.appearance = createdChar.appearance;
      player.personality = createdChar.personality;
      player.job = '무직 (초보자)';
      player.role = 'tank'; // 기본 물리 모션 베이스 (성내 교관 대화 시 직업/역할 전직)
      
      const allocatedBonus = {
        str: createdChar.stats.str - TERRA_ANIMA_RACES[createdChar.race].baseStats.str,
        con: createdChar.stats.con - TERRA_ANIMA_RACES[createdChar.race].baseStats.con,
        dex: createdChar.stats.dex - TERRA_ANIMA_RACES[createdChar.race].baseStats.dex,
        qui: createdChar.stats.qui - TERRA_ANIMA_RACES[createdChar.race].baseStats.qui,
        int: createdChar.stats.int - TERRA_ANIMA_RACES[createdChar.race].baseStats.int,
        pie: createdChar.stats.pie - TERRA_ANIMA_RACES[createdChar.race].baseStats.pie,
        cha: createdChar.stats.cha - TERRA_ANIMA_RACES[createdChar.race].baseStats.cha,
      };

      initCharacterSheet(
        player,
        createdChar.race,
        allocatedBonus,
        1
      );

      // Save newly created character to account storage
      const accKey = AccountSaveSystem.getAccountKey(mainMenu.getCurrentUser());
      AccountSaveSystem.saveNewCharacter(accKey, {
        name: createdChar.name,
        race: createdChar.race,
        stats: allocatedBonus,
        personality: createdChar.personality,
        appearance: createdChar.appearance,
      }).then((savedSlot) => {
        currentSlotId = null;

        // 생성 완료 -> 캐릭터 선택 페이지로 (선택 후 월드 접속)
        gameState = 'MAIN_MENU';
        setWorldUIActive(false);
        mainMenu.show();
        mainMenu.openCharacterSelectModal(savedSlot.id);
      }).catch((err) => {
        console.error('[CharacterCreation] Failed to save character:', err);
        gameState = 'MAIN_MENU';
        setWorldUIActive(false);
        mainMenu.show();
        mainMenu.openCharacterSelectModal();
      });
    },
    () => {
      // User cancelled
      gameState = 'MAIN_MENU';
      mainMenu.show();
    }
  );
}

/** 저장된 슬롯의 인벤토리를 플레이어 20칸 인벤토리에 반영 (관리자 시작 지급 아이템 포함) */
function loadSlotInventory(entity: EntityState, items?: any[]): void {
  initEntityInventory20(entity);
  const inv = entity.inventory!;
  for (let i = 0; i < inv.length; i++) inv[i] = null;
  (items || [])
    .filter((it) => it && it.name)
    .slice(0, inv.length)
    .forEach((it, idx) => {
      inv[idx] = {
        id: it.id || `item_${Date.now().toString(36)}_${idx}`,
        name: it.name,
        icon: it.icon || '📦',
        category: it.category || 'material',
        rarity: it.rarity || 'common',
        quantity: it.quantity || 1,
        maxStack: it.maxStack || 99,
        weightKg: it.weightKg ?? 0.5,
        valueGold: it.valueGold ?? 0,
        description: it.description || '',
      };
    });
}

function enterWorldWithPlayer(): void {
  net.connect(() => {
    const p = registry.getPlayer() || player;
    return {
      name: p.name,
      job: p.job || '',
      role: (p.role as string) || 'tank',
      level: p.level ?? 1,
      appearance: p.appearance ?? null,
      zoneId: renderer.getActiveZoneId(),
      x: p.x,
      y: p.y,
      hp: p.resources.hp,
      maxHp: p.resources.maxHp,
    };
  });
  ActivityLog.log('WORLD_ENTERED', AccountSaveSystem.getAccountKey(mainMenu.getCurrentUser()), '월드 접속 완료', player.name);
  gameState = 'IN_WORLD';
  setWorldUIActive(true);
  updateTopNavBar();
  console.log('⚔️ [Terra Anima] In-World Exploration Started!');
}

splashScreen.init(() => {
  console.log('✨ [SplashScreen] Closed -> Opening Main Menu');
  gameState = 'MAIN_MENU';
  mainMenu.show();
});

// Initialize Main Menu Controller
mainMenu.init({
  onNewGame: () => {
    console.log('🎮 [MainMenu] New Game Selected -> Launching Character Creation Flow');
    gameState = 'CHAR_CREATE';
    soundManager.getAmbient().setMute(true); // 캐릭터 생성 중 앰비언트 사운드 무음
    mainMenu.hide();
    startCharacterCreationFlow();
  },
  onContinue: (selectedSlot: SavedCharacterSlot) => {
    console.log('🎮 [MainMenu] Continue Game Selected with Character:', selectedSlot);
    currentSlotId = selectedSlot.id;
    
    // Load saved character data into player entity
    player.name = selectedSlot.name;
    if (selectedSlot.appearance) {
      player.appearance = selectedSlot.appearance;
    }
    if (selectedSlot.personality) {
      player.personality = selectedSlot.personality;
    }
    player.job = selectedSlot.class || '무직 (초보자)';
    if (selectedSlot.class === 'admin' || (selectedSlot as any).role === 'admin') {
      player.role = 'admin';
      (player as any).isAdmin = true;
    }
    if (selectedSlot.gold !== undefined) {
      player.gold = selectedSlot.gold;
    }

    const race = selectedSlot.race || 'solarian';
    const stats = selectedSlot.stats || { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 };
    initCharacterSheet(player, race, stats, selectedSlot.level || 1);
    if (selectedSlot.exp !== undefined) {
      player.exp = selectedSlot.exp;
      player.maxExp = Math.max(100, (player.level || 1) * 1000);
    }
    if (selectedSlot.unlockedSlots !== undefined) {
      player.unlockedSlots = Math.min(50, Math.max(20, selectedSlot.unlockedSlots));
    }
    loadSlotInventory(player, selectedSlot.inventory);

    if (selectedSlot.zoneId !== undefined && selectedSlot.zoneId !== renderer.getActiveZoneId()) {
      teleportPartyToZone(selectedSlot.zoneId, selectedSlot.x || 80, selectedSlot.y || 80);
    } else if (selectedSlot.x !== undefined && selectedSlot.y !== undefined) {
      player.x = selectedSlot.x;
      player.y = selectedSlot.y;
    }

    gameState = 'IN_WORLD';
    mainMenu.hide();
    enterWorldWithPlayer();
  },
  onShop: () => {
    console.log('💎 [MainMenu] Shop Selected');
    royalShopModal.open();
  },
});

// 1. Initialize Registry & Infrastructure
const registry = new EntityRegistry();
(window as any).__TERRA_REGISTRY__ = registry;

// 2. Setup Single Player Entity (No Party Companions, Starting Solo in Zone 1)
const player: EntityState = {
  id: 'player_hero',
  name: '모험가',
  category: 'player',
  role: 'tank',
  job: '무직 (초보자)',
  x: 80,
  y: 80,
  vx: 0,
  vy: 0,
  dir: 'S',
  speed: 4.2,
  resources: {
    hp: 100,
    maxHp: 100,
    sp: 100,
    maxSp: 100,
    mp: 100,
    maxMp: 100,
    conc: 100,
    maxConc: 100,
  },
  buffs: [],
  cc: null,
  cast: null,
  isDead: false,
  color: '#38bdf8',
  weaponType: 'crush',
  cooldowns: {},
  targetId: null,
  inCombat: false,
  avatarIcon: '🧙',
  appearance: {
    skinColor: '#fde68a',
    hairStyle: 'short',
    hairColor: '#facc15',
    facialFeature: 'none',
    eyeColor: '#0f172a',
    primaryArmorColor: '#38bdf8',
    secondaryArmorColor: '#fde047',
    capeColor: '#1e3a8a',
    showHelm: false,
    bodyScale: 1.0,
    titlePrefix: '솔라리스 초보자길드',
    guild: '솔라리스 초보자길드',
  },
};
registry.register(player);

// 2-1. Initialize Clean Character Stats (Single Player)
initCharacterSheet(player, 'solarian', { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 }, 1);
initEntityInventory20(player);
player.personality = computePersonalityProfile([0, 0, 0, 0, 0, 0, 0, 0]);

// 3. World Entities & Zone Monsters
function spawnZoneMonsters(): void {
  const dragon: EntityState = {
    id: 'mob_dragon_boss',
    name: '🐲 고대 화염룡 드라고니스',
    category: 'monster',
    role: 'monster',
    job: '신화 레이드 보스',
    x: 91,
    y: 91,
    vx: 0,
    vy: 0,
    dir: 'NW',
    speed: 1.8,
    resources: {
      hp: 1500,
      maxHp: 1500,
      sp: 300,
      maxSp: 300,
      mp: 500,
      maxMp: 500,
      conc: 100,
      maxConc: 100,
    },
    buffs: [],
    cc: null,
    cast: null,
    isDead: false,
    color: '#dc2626',
    weaponType: 'holy',
    cooldowns: {},
    threatTable: {},
    targetId: null,
    inCombat: false,
    avatarIcon: '🐲',
  };
  registry.register(dragon);
}
spawnZoneMonsters();

function spawnWorldResources(): void {
  // Can be populated cleanly or placed via GM Tool
}
spawnWorldResources();

// 4. Interactive World NPCs (성내 직업 교관 및 편의 NPC)
function spawnWorldNPCs(): void {
  const npcConfigs: Array<{
    id: string;
    name: string;
    job: string;
    role: EntityState['role'];
    icon: string;
    x: number;
    y: number;
    primaryColor: string;
    secondaryColor: string;
  }> = [
    {
      id: 'npc_tutorial_brendan',
      name: '왕국 안내원 브렌든 (Brendan)',
      job: '초보자 안내 가이드',
      role: 'buffer',
      icon: '📜',
      x: 82,
      y: 82,
      primaryColor: '#0284c7',
      secondaryColor: '#fde047',
    },
    {
      id: 'npc_trainer_leon',
      name: '기사단장 레온 (Sir Leon)',
      job: '전사/성기사 직업 교관',
      role: 'tank',
      icon: '⚔️',
      x: 35,
      y: 36,
      primaryColor: '#1d4ed8',
      secondaryColor: '#facc15',
    },
    {
      id: 'npc_enchanter_elara',
      name: '대인챈터 엘라라 (Elara)',
      job: '솔라리스 왕실 인챈터',
      role: 'caster_dps',
      icon: '🔮',
      x: 42,
      y: 34,
      primaryColor: '#7e22ce',
      secondaryColor: '#38bdf8',
    },
    {
      id: 'npc_trainer_marta',
      name: '사제장 마르타 (Marta)',
      job: '치유 사제 직업 교관',
      role: 'healer',
      icon: '✨',
      x: 38,
      y: 40,
      primaryColor: '#059669',
      secondaryColor: '#fde047',
    },
    {
      id: 'npc_stable_jackson',
      name: '마구간지기 잭슨 (Jackson)',
      job: '솔라리스 왕실 마구간지기',
      role: 'ranged_dps',
      icon: '🐎',
      x: 76,
      y: 83,
      primaryColor: '#b45309',
      secondaryColor: '#fde047',
    },
  ];

  for (const cfg of npcConfigs) {
    const npc: EntityState = {
      id: cfg.id,
      name: cfg.name,
      category: 'npc',
      role: cfg.role,
      job: cfg.job,
      x: cfg.x,
      y: cfg.y,
      vx: 0,
      vy: 0,
      dir: 'S',
      speed: 0,
      gold: Currency.toCopper({ gold: 10 }),
      appearance: {
        skinColor: '#fde68a',
        hairStyle: 'short',
        hairColor: '#78350f',
        facialFeature: 'none',
        eyeColor: '#0f172a',
        primaryArmorColor: cfg.primaryColor,
        secondaryArmorColor: cfg.secondaryColor,
        capeColor: cfg.primaryColor,
        showHelm: false,
        bodyScale: 1.0,
        titlePrefix: cfg.job,
      },
      resources: {
        hp: 500,
        maxHp: 500,
        sp: 100,
        maxSp: 100,
        mp: 200,
        maxMp: 200,
        conc: 100,
        maxConc: 100,
      },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: cfg.primaryColor,
      weaponType: 'crush',
      cooldowns: {},
      targetId: null,
      inCombat: false,
      avatarIcon: cfg.icon,
    };
    initEntityInventory20(npc);
    registry.register(npc);
  }
}
spawnWorldNPCs();

// 5. Sound & Audio System
const soundManager = new SoundManager();

// 6. Gathering & Harvesting System
const gatheringModule = new GatheringModule(registry);

// 7. Player Switching System (Supports 6 Classes)
function switchPlayerCharacter(newPlayerId: string): void {
  const currentLeader = registry.getPlayer();
  if (currentLeader && currentLeader.id === newPlayerId) return;

  const targetEntity = registry.get(newPlayerId);
  if (!targetEntity || targetEntity.category === 'monster' || targetEntity.category === 'resource') return;

  if (currentLeader) {
    currentLeader.category = 'companion';
    currentLeader.vx = 0;
    currentLeader.vy = 0;
    currentLeader.targetId = null;
  }

  targetEntity.category = 'player';
  targetEntity.targetId = null;

  soundManager.playJobSwitch();

  // Log transition
  console.log(`👑 [플레이어 전환] -> ${targetEntity.name} (${targetEntity.job})`);
  EventBus.emit('LOG_EMITTED', {
    id: Math.random().toString(),
    timestamp: performance.now(),
    text: `👑 [직업 전환] 이제 [${targetEntity.name}] 캐릭터로 직접 조작합니다! (직업: ${targetEntity.job})`,
    category: 'system',
  });

  updateTopNavBar();
  if (typeof charSheetHud !== 'undefined' && charSheetHud) {
    charSheetHud.refreshIfOpen();
  }
}

// 8. Initialize Core Modules & UI
const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
const combatModule = new CombatModule(registry);
const renderer = new IsoRenderer(canvas, registry);

const targetFrame = new TargetFrameHUD(
  () => combatModule.toggleStick(),
  (targetName) => {
    const res = partyManager.invite(targetName);
    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: Date.now(),
      text: res.message,
      category: 'system',
    });
  },
  (targetEntity) => {
    const activePlayer = registry.getPlayer();
    if (activePlayer && targetEntity) {
      tradeModalInstance.open(activePlayer, targetEntity);
    }
  }
);
targetFrame.mount(document.body);
HudLayout.wrap(document.getElementById('target-frame-hud')!, { id: 'target', mode: 'scale', anchor: 'tc', dx: 0, dy: 10, w: 320, scale: 1 });

const input = new InputController(
  combatModule,
  registry,
  canvas,
  () => renderer.getCameraOffset(),
  (id) => switchPlayerCharacter(id),
  () => renderer.getZoom(),
  (x, y) => renderer.setHoverTile(x, y),
  (targetResource) => {
    const activePlayer = registry.getPlayer();
    if (activePlayer) {
      gatheringModule.startGathering(activePlayer, targetResource);
    }
  },
  () => gameState
);

const partyHud = new PartyHUD(
  (allyId) => combatModule.selectTarget(allyId),
  (allyId) => switchPlayerCharacter(allyId)
);
partyHud.mount(document.body);
HudLayout.wrap(document.getElementById('party-hud')!, { id: 'player', mode: 'scale', anchor: 'br', dx: 10, dy: 10, w: 300, scale: 1 });

const actionBar = new ActionBarHUD((slot) => {
  combatModule.triggerPlayerSkill(slot);
});
actionBar.mount(document.body);
HudLayout.wrap(document.getElementById('action-bar-hud')!, { id: 'quickbar', mode: 'scale', anchor: 'bc', dx: 30, dy: 10, scale: 0.75 });

const combatLog = new CombatLogHUD();
combatLog.mount(document.body);
HudLayout.wrap(document.getElementById('combat-log-hud')!, { id: 'chat', mode: 'free', anchor: 'bl', dx: 10, dy: 10, w: 430, h: 190, minW: 220, minH: 90 });
const combatStandaloneEl = document.getElementById('combat-standalone-hud');
if (combatStandaloneEl) {
  HudLayout.wrap(combatStandaloneEl, { id: 'combat_standalone', mode: 'free', anchor: 'bl', dx: 10, dy: 205, w: 430, h: 140, minW: 220, minH: 70 });
}
const groupHud = new GroupHUD();
groupHud.mount(document.body);

// Mobile Virtual D-Pad Joystick & Quick Touch Controls HUD
const touchControls = new TouchControlsHUD(
  (screenDx, screenDy) => {
    input.setTouchMoveVector(screenDx, screenDy);
  },
  () => combatModule.toggleStick(),
  () => combatModule.cycleTarget(true)
);
touchControls.mount(document.body);

// 9. Mount Character / Race / Stat / Skill / Multi-Skill Crafting / Enchanter / 7-Zone Map Inspector HUD
let zoneTransitionCooldown = 0;

function teleportPartyToZone(zoneId: number, spawnX = 80, spawnY = 80): void {
  renderer.setActiveZone(zoneId);
  const zData = getZoneData(zoneId);
  zoneTransitionCooldown = 1.5;

  // 1. Relocate Player & Party Companions to arrival coordinates in formation
  const activeLeader = registry.getPlayer() || player;
  activeLeader.x = spawnX;
  activeLeader.y = spawnY;
  activeLeader.vx = 0;
  activeLeader.vy = 0;
  activeLeader.targetId = null;

  const partyOffsets = [
    { dx: -0.75, dy: -0.65 },
    { dx: 0.75, dy: -0.65 },
    { dx: -1.1, dy: -1.35 },
    { dx: 0.0, dy: -1.35 },
    { dx: 1.1, dy: -1.35 },
  ];
  let compIdx = 0;
  for (const member of registry.getParty()) {
    if (member.id === activeLeader.id) continue;
    const off = partyOffsets[compIdx % partyOffsets.length];
    member.x = spawnX + off.dx;
    member.y = spawnY + off.dy;
    member.vx = 0;
    member.vy = 0;
    member.targetId = null;
    compIdx++;
  }

  // 2. Trigger Teleport Arrival Visual Effect
  EventBus.emit('SPELL_EFFECT_TRIGGERED', {
    type: 'blink_arrival',
    gx: spawnX,
    gy: spawnY,
    radius: 2.2,
  });

  // 3. Sync Weather & Season to match the Zone's Climate
  const env = renderer.getEnvironment();
  if (zoneId === 1) env.setWeatherAndSeason('clear', 'spring');
  else if (zoneId === 2) env.setWeatherAndSeason('clear', 'autumn');
  else if (zoneId === 3) env.setWeatherAndSeason('clear', 'spring');
  else if (zoneId === 4) env.setWeatherAndSeason('snow', 'winter');
  else if (zoneId === 5) env.setWeatherAndSeason('fog', 'autumn');
  else if (zoneId === 6) env.setWeatherAndSeason('clear', 'spring');
  else if (zoneId === 7) env.setWeatherAndSeason('clear', 'spring');
  else if (zoneId === 8) env.setWeatherAndSeason('clear', 'spring');
  else if (zoneId === 9) env.setWeatherAndSeason('clear', 'spring');
  else if (zoneId >= 10) env.setWeatherAndSeason('fog', 'winter');

  // 4. Update & Respawn ONLY the Zone's World Boss (Regular field mobs removed)
  const zoneBosses: Record<number, { boss: string; bossIcon: string }> = {
    1: { boss: '🐲 고대 화염룡 드라고니스', bossIcon: '🐲' },
    2: { boss: '🗿 고대 크롬레크 수호신', bossIcon: '🗿' },
    3: { boss: '🌲 실바나 고분 망령군주', bossIcon: '👻' },
    4: { boss: '👑 스노우자이언트 (Snow Giant)', bossIcon: '🧊' },
    5: { boss: '🐍 심연의 늪지 히드라', bossIcon: '🐍' },
    6: { boss: '🐲 불의 용 이그나르 (Ignar the Dragon)', bossIcon: '🐲' },
    7: { boss: '🔮 바란 경 (Sir Baran the Wraith)', bossIcon: '👻' },
    8: { boss: '👑 솔라리스 수호 대신', bossIcon: '👑' },
    9: { boss: '🏡 하우징 지구 집사', bossIcon: '🏡' },
    10: { boss: '💀 해골 정예 백부장', bossIcon: '💀' },
    11: { boss: '👻 심연의 망령 군주', bossIcon: '👻' },
    12: { boss: '💀 [3대 보스] 해골화된 루나리스 병사 & 좀비화된 루나리스 병사 & 흡혈박쥐', bossIcon: '👑' },
  };
  const bConf = zoneBosses[zoneId] || zoneBosses[1];

  const boss = registry.get('mob_dragon_boss');
  if (boss) {
    boss.isDead = false;
    boss.x = spawnX + 11;
    boss.y = spawnY + 11;
    boss.resources.hp = boss.resources.maxHp;
    boss.name = bConf.boss;
    boss.avatarIcon = bConf.bossIcon;
    boss.threatTable = {};
  }

  // 5. Spawn Dungeon Floor Monsters if in Cemetery Catacombs (Zones 10, 11, 12)
  if (zoneId === 10 || zoneId === 11 || zoneId === 12) {
    const floor = (zoneId - 9) as 1 | 2 | 3;
    const cMonsters = getCemeteryMonsterConfigs(floor);
    for (const cm of cMonsters) {
      if (!registry.get(cm.id)) {
        registry.register(cm);
      } else {
        const existing = registry.get(cm.id)!;
        existing.isDead = false;
        existing.resources.hp = existing.resources.maxHp;
        existing.x = cm.x;
        existing.y = cm.y;
      }
    }
  }

  if (typeof charSheetHud !== 'undefined' && charSheetHud) {
    charSheetHud.setCurrentZoneId(zoneId);
  }

  EventBus.emit('LOG_EMITTED', {
    id: Math.random().toString(),
    timestamp: performance.now(),
    text: `🗺️ [오픈월드 존 이동 완료!] ➔ [Zone ${zoneId}. ${zData.name} (${zData.englishName})] 좌표(${Math.round(spawnX)}, ${Math.round(spawnY)})에 도착했습니다!`,
    category: 'system',
  });
  updateTopNavBar();
}

const stableRouteSystem = new StableRouteSystem(
  registry,
  () => renderer.getActiveZoneId(),
  (zId, sx, sy) => teleportPartyToZone(zId, sx, sy),
  () => {
    updateTopNavBar();
    updateStableRideBanner();
    if (typeof charSheetHud !== 'undefined' && charSheetHud) {
      charSheetHud.refreshIfOpen();
    }
  }
);

const charSheetHud = new CharacterWindows(
  registry,
  (zId) => teleportPartyToZone(zId, 80, 80),
  stableRouteSystem
);
charSheetHud.mount(document.body);
// 실시간 서버(WebSocket): 다른 플레이어 표시 + 파티 메시지 중계
const net = new NetClient();
const remotePlayers = new RemotePlayers(registry);
registry.setRemoteProvider(() => remotePlayers.list);
renderer.setRemotePlayers(remotePlayers.list);
(window as any).__TERRA_REMOTE__ = remotePlayers; // 디버그/테스트용
net.onPlayers = (list) => remotePlayers.replaceAll(list);
net.onJoin = (p) => remotePlayers.upsert(p);
net.onLeave = (id) => remotePlayers.remove(id);
net.onMove = (id, x, y, hp, maxHp) => remotePlayers.move(id, x, y, hp, maxHp);
net.onReset = () => remotePlayers.clear();
net.onStatus = (text) =>
  EventBus.emit('LOG_EMITTED', { id: Math.random().toString(), timestamp: Date.now(), text: `[접속] ${text}`, category: 'system' } as any);

// 중복/멀티 인스턴스 접속 감지 시 경고 모달 표시 및 닫기 처리
net.onKicked = (_reason) => {
  const modalId = 'multi-instance-kicked-modal';
  if (document.getElementById(modalId)) return;

  const overlay = document.createElement('div');
  overlay.id = modalId;
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:999999;display:flex;align-items:center;justify-content:center;background:rgba(2,6,15,0.85);backdrop-filter:blur(8px);';

  overlay.innerHTML = `
    <div style="background:linear-gradient(135deg, #1e1b4b, #0f172a); border:2px solid #ef4444; border-radius:12px; padding:24px 28px; width:360px; max-width:90vw; text-align:center; box-shadow:0 12px 36px rgba(0,0,0,0.8); color:#f8fafc; font-family:sans-serif;">
      <div style="font-size:36px; margin-bottom:12px;">⚠️</div>
      <div style="font-size:17px; font-weight:800; color:#fca5a5; margin-bottom:10px; letter-spacing:0.5px;">
        멀티 인스턴스입니다 종료합니다
      </div>
      <p style="font-size:12.5px; color:#cbd5e1; line-height:1.5; margin-bottom:20px;">
        다른 브라우저 또는 탭에서 동일한 캐릭터로 접속하여<br/>현재 연결을 안전하게 종료합니다.
      </p>
      <button id="btn-close-multi-instance" style="background:linear-gradient(135deg, #dc2626, #b91c1c); border:1px solid #f87171; color:#fff; font-size:14px; font-weight:bold; padding:10px 24px; border-radius:6px; cursor:pointer; width:100%; box-shadow:0 4px 12px rgba(220,38,38,0.4);">
        확인 (창 닫기)
      </button>
    </div>
  `;

  document.body.appendChild(overlay);

  overlay.querySelector('#btn-close-multi-instance')?.addEventListener('click', () => {
    try {
      window.close();
    } catch { /* ignore */ }
    // 스크립트로 열지 않은 일반 탭이라 window.close()가 차단되는 경우 안내 및 화면 비활성화
    setTimeout(() => {
      overlay.innerHTML = `
        <div style="background:#0f172a; border:1px solid #475569; border-radius:8px; padding:20px; text-align:center; color:#94a3b8; font-size:14px;">
          접속이 종료되었습니다. 브라우저 탭을 닫아주세요.
        </div>
      `;
      // 페이지를 빈 페이지나 메인 메뉴로 리다이렉트
      try {
        location.href = 'about:blank';
      } catch { /* ignore */ }
    }, 150);
  });
};

// 실시간 서버 버전 감지 연동
net.onVersion = (serverVersion) => {
  if (serverVersion && serverVersion !== CLIENT_VERSION) {
    ScreenNoticeHUD.show(
      '🔄 [클라이언트 업데이트 알림]',
      `새로운 버전(v${serverVersion})이 배포되었습니다. 잠시 후 안내 팝업이 표시됩니다.`,
      6,
      'update'
    );
    VersionChecker.triggerUpdatePrompt(serverVersion);
  }
};

// 실시간 멀티플레이어 채팅 연동
net.onChat = (m) => {
  let cat: any = 'chat_all';
  let prefix = `[일반] ${m.from}: `;
  if (m.channel === 'normal') {
    cat = 'chat_normal';
    prefix = `[일반] ${m.from}: `;
  } else if (m.channel === 'party') {
    cat = 'chat_party';
    prefix = `[파티] ${m.from}: `;
  } else if (m.channel === 'guild') {
    cat = 'chat_guild';
    prefix = `[길드] ${m.from}: `;
  } else if (m.channel === 'broadcast') {
    cat = 'chat_broadcast';
    prefix = `[방송] ${m.from}: `;
    // 지역 방송 시 화면 상단에도 알림 배너 노출
    ScreenNoticeHUD.show(`📢 [지역 방송] ${m.from}`, m.text, 5, 'notice');
  } else if (m.channel === 'world') {
    cat = 'chat_world';
    prefix = `[월드] ${m.from}: `;
    // 전 월드 공지나 점검 알림 시 화면 중앙 상단에 큰 푸시 배너 노출!
    const isWarning = m.text.includes('점검') || m.text.includes('긴급') || m.text.includes('주의');
    ScreenNoticeHUD.show(
      `📢 [월드 공지] ${m.from}`,
      m.text,
      7,
      isWarning ? 'warning' : 'notice'
    );
  } else if (m.channel === 'whisper') {
    cat = 'chat_whisper';
    prefix = m.to ? `[귓속말 -> ${m.to}]: ` : `[귓속말 <- ${m.from}]: `;
  } else if (m.channel === 'system') {
    cat = 'system';
    prefix = `[알림] `;
  }
  EventBus.emit('LOG_EMITTED', {
    id: Math.random().toString(),
    timestamp: Date.now(),
    text: `${prefix}${m.text}`,
    category: cat,
  });
};

// 실시간 몬스터 체력 및 피격 동기화
net.onMobHit = (m) => {
  const target = registry.get(m.mobId);
  if (target && target.category === 'monster' && !target.isDead) {
    target.resources.hp = Math.max(0, m.remainingHp);
    if (target.resources.hp <= 0) {
      target.isDead = true;
    }
  }
};

net.onMobDead = (m) => {
  const target = registry.get(m.mobId);
  if (target && target.category === 'monster') {
    target.resources.hp = 0;
    target.isDead = true;
    registry.markForDespawn(target.id, 4.0);
  }
};

// 내가 몬스터를 공격/타격했을 때 웹소켓으로 다른 유저들에게 동기화 브로드캐스트
EventBus.on('COMBAT_HIT', (e) => {
  const target = registry.get(e.targetId);
  if (target && target.category === 'monster' && net.isOpen()) {
    net.sendMobHit(target.id, e.damage, target.resources.hp);
  }
});

// 내가 몬스터를 처치했을 때 웹소켓으로 사망 동기화 및 파티 보상 브로드캐스트
EventBus.on('UNIT_DIED', (e) => {
  const victim = registry.get(e.victimId);
  if (victim && victim.category === 'monster') {
    const isBoss = victim.id.includes('boss') || victim.resources.maxHp >= 1000;
    const expGain = isBoss ? 200 : 45;
    const p = registry.getPlayer() || player;
    p.exp = (p.exp || 0) + expGain;
    const reqExp = p.maxExp || Math.max(100, (p.level || 1) * 1000);
    p.maxExp = reqExp;
    if (p.exp >= reqExp && (p.level || 1) < 50) {
      p.exp -= reqExp;
      p.level = (p.level || 1) + 1;
      p.maxExp = Math.max(100, p.level * 1000);
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: `🎉 [레벨 업!] [${p.name}] 캐릭터가 Lv.${p.level}에 도달했습니다!`,
        category: 'system',
      });
    }

    if (net.isOpen()) {
      net.sendMobDead(victim.id);
      if (partyManager.isInParty()) {
        const fameGain = isBoss ? 250 : 80;
        const copperGain = isBoss ? Currency.toCopper({ gold: 10 }) : Currency.toCopper({ silver: 50 });
        partyManager.broadcastReward(copperGain, fameGain, victim.name);
      }
    }
  }
});

const partyManager = new PartyManager(registry, new NetPartyTransport(net));

// CombatLogHUD 채팅 입력기 핸들러 연결
combatLog.setSendHandler((channel, text, to) => {
  if (!net.isOpen()) {
    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: Date.now(),
      text: '[시스템] 오프라인 상태에서는 채팅을 보낼 수 없습니다.',
      category: 'system',
    });
    return;
  }
  const p = registry.getPlayer() || player;
  const myGuild = (p?.appearance?.guild as string) || '솔라리스 초보자길드';
  if (channel === 'party') {
    if (!partyManager.isInParty()) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: Date.now(),
        text: '[시스템] 파티에 소속되어 있지 않습니다.',
        category: 'system',
      });
      return;
    }
    net.sendChat('party', text, undefined, partyManager.getMembers());
  } else if (channel === 'whisper') {
    net.sendChat('whisper', text, to);
  } else if (channel === 'guild') {
    net.sendChat('guild', text, undefined, undefined, myGuild);
  } else if (channel === 'broadcast') {
    net.sendChat('broadcast', text);
  } else if (channel === 'world') {
    net.sendChat('world', text);
  } else {
    // normal
    net.sendChat('normal', text);
  }
});
const partyWindow = new PartyWindow(registry, partyManager);
partyWindow.mount(document.body);

const guildWindow = new GuildWindow(registry);
guildWindow.mount(document.body);

housingModeController.init(
  registry,
  renderer,
  () => renderer.getActiveZoneId(),
  () => Boolean((window as any).TerraAnimaGM)
);

housingTopViewHUD.init(
  registry,
  () => renderer.getActiveZoneId(),
  () => Boolean((window as any).TerraAnimaGM)
);

const adminConsole = new AdminConsoleHUD(
  registry,
  renderer,
  (zId, sx, sy) => teleportPartyToZone(zId, sx, sy)
);
adminConsole.mount(document.body);

// Floating DAoC Stable-to-Stable Automated Horse Ride HUD Banner
const stableRideBannerEl = document.createElement('div');
stableRideBannerEl.id = 'stable-ride-hud';
stableRideBannerEl.style.position = 'absolute';
stableRideBannerEl.style.top = '50px';
stableRideBannerEl.style.left = '50%';
stableRideBannerEl.style.transform = 'translateX(-50%)';
stableRideBannerEl.style.zIndex = '160';
stableRideBannerEl.style.display = 'none';
stableRideBannerEl.style.background = 'linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,27,75,0.96))';
stableRideBannerEl.style.border = '2px solid #f59e0b';
stableRideBannerEl.style.borderRadius = '10px';
stableRideBannerEl.style.padding = '10px 18px';
stableRideBannerEl.style.color = '#f8fafc';
stableRideBannerEl.style.boxShadow = '0 10px 30px rgba(0,0,0,0.75), 0 0 18px rgba(245,158,11,0.35)';
stableRideBannerEl.style.minWidth = '480px';
stableRideBannerEl.style.fontFamily = "'Segoe UI', sans-serif";
document.body.appendChild(stableRideBannerEl);

function updateStableRideBanner(): void {
  const st = stableRouteSystem.getRouteStatus();
  if (!st.active) {
    stableRideBannerEl.style.display = 'none';
    return;
  }
  stableRideBannerEl.style.display = 'block';
  const pct = Math.round(st.progressPct);
  const p = registry.getPlayer() || player;
  stableRideBannerEl.innerHTML = `
    <div style="display:flex; justify-content:space-between; align-items:center; gap:14px; margin-bottom:6px;">
      <div style="display:flex; align-items:center; gap:8px;">
        <span style="font-size:20px;">🐎</span>
        <div>
          <div style="font-size:12px; font-weight:bold; color:#fde047;">
            [마구간 자동 승마 주행 중] ${st.fromName || '출발지'} ➔ ${st.toName || '도착지'}
          </div>
          <div style="font-size:10.5px; color:#94a3b8;">
            가도 웨이포인트 자동 추적 주행 중 · 현재 좌표 (${Math.round(p.x)}, ${Math.round(p.y)}) · 진행률 <b style="color:#34d399;">${pct}%</b>
          </div>
        </div>
      </div>
      <div style="display:flex; align-items:center; gap:5px;">
        <span style="font-size:10px; color:#cbd5e1;">배속:</span>
        ${[1, 2, 4]
          .map(
            (spd) => `
          <button data-ride-spd="${spd}" style="
            background:${st.speedMult === spd ? '#d97706' : '#1e293b'};
            border:1px solid ${st.speedMult === spd ? '#fde047' : '#475569'};
            color:#fff; padding:3px 7px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;
          ">${spd}x</button>
        `
          )
          .join('')}
        <button id="btn-banner-dismount" style="
          background:linear-gradient(135deg, #dc2626, #991b1b);
          border:1.5px solid #fca5a5;
          color:#ffffff; padding:4px 10px; border-radius:5px; cursor:pointer; font-size:11px; font-weight:bold; margin-left:6px;
        ">⏬ 중도 하차 [Space]</button>
      </div>
    </div>
    <div style="width:100%; height:7px; background:#0f172a; border-radius:4px; overflow:hidden; border:1px solid #334155;">
      <div style="width:${pct}%; height:100%; background:linear-gradient(90deg, #f59e0b, #34d399); transition:width 0.1s;"></div>
    </div>
  `;

  stableRideBannerEl.querySelectorAll('[data-ride-spd]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const spd = parseInt(btn.getAttribute('data-ride-spd') || '1', 10);
      stableRouteSystem.setSpeedMultiplier(spd);
    });
  });

  stableRideBannerEl.querySelector('#btn-banner-dismount')?.addEventListener('click', () => {
    stableRouteSystem.dismountMidRoute();
  });
}

// ===== ESC 시스템 메뉴 (메인메뉴로[저장] / 설정 / 종료) =====
let currentSlotId: string | null = null;
let escMenuEl: HTMLElement | null = null;

function saveCurrentCharacter(): void {
  if (!currentSlotId) return;
  const accKey = AccountSaveSystem.getAccountKey(mainMenu.getCurrentUser());
  const all = AccountSaveSystem.loadAllAccounts();
  // 슬롯이 실제 저장된 계정 키를 찾는다
  const ownerKey = Object.keys(all).find((k) => all[k]?.slots?.some((s) => s.id === currentSlotId)) || accKey;
  AccountSaveSystem.updateCharacter(ownerKey, currentSlotId, {
    gold: player.gold,
    level: player.level,
    exp: player.exp,
    unlockedSlots: player.unlockedSlots || 20,
    inventory: (player.inventory || []).filter(Boolean),
    zoneId: renderer.getActiveZoneId(),
    x: player.x,
    y: player.y,
  });
  console.log(`💾 [Save] 캐릭터 저장 완료: ${player.name}`);
}

function closeEscMenu(): void {
  if (escMenuEl) escMenuEl.style.display = 'none';
}

function openEscMenu(): void {
  if (!escMenuEl) {
    escMenuEl = document.createElement('div');
    escMenuEl.style.cssText =
      'position:fixed;inset:0;z-index:99999;display:none;align-items:center;justify-content:center;background:rgba(0,0,0,0.6);';
    const box = document.createElement('div');
    box.style.cssText =
      'background:#1e293b;border:2px solid #f59e0b;border-radius:12px;padding:24px;min-width:280px;display:flex;flex-direction:column;gap:12px;color:#fff;font-family:inherit;';
    const mk = (label: string, fn: () => void) => {
      const b = document.createElement('button');
      b.textContent = label;
      b.style.cssText =
        'padding:12px;font-size:1rem;border-radius:8px;border:1px solid #475569;background:#334155;color:#fff;cursor:pointer;';
      b.onclick = fn;
      box.appendChild(b);
    };
    const title = document.createElement('div');
    title.textContent = '⏸️ 시스템 메뉴';
    title.style.cssText = 'font-size:1.2rem;font-weight:bold;text-align:center;color:#fde047;';
    box.appendChild(title);
    mk('🏠 메인메뉴로 (캐릭터 저장)', () => {
      if (!confirm('캐릭터를 저장하고 메인메뉴로 돌아가시겠습니까?')) return;
      saveCurrentCharacter();
      closeEscMenu();
      currentSlotId = null;
      net.disconnect();
      gameState = 'MAIN_MENU';
      setWorldUIActive(false);
      mainMenu.show();
    });
    mk('🔲 HUD 위치 초기화', () => {
      HudLayout.resetAll();
      closeEscMenu();
    });
    mk('⚙️ 설정', () => {
      closeEscMenu();
      mainMenu.openSettingsOverlay(() => openEscMenu());
    });
    mk('🚪 종료', () => {
      if (!confirm('게임을 종료하시겠습니까?\n(현재 캐릭터는 저장됩니다)')) return;
      saveCurrentCharacter();
      window.close();
    });
    mk('▶️ 계속하기', closeEscMenu);
    escMenuEl.appendChild(box);
    document.body.appendChild(escMenuEl);
  }
  escMenuEl.style.display = 'flex';
}

window.addEventListener('keydown', (e) => {
  if (gameState !== 'IN_WORLD' || e.key !== 'Escape') return;
  if (escMenuEl && escMenuEl.style.display === 'flex') {
    closeEscMenu();
  } else {
    openEscMenu();
  }
});

window.addEventListener('keydown', (e) => {
  if (gameState !== 'IN_WORLD') return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (e.code === 'Space' && stableRouteSystem.isRiding()) {
    e.preventDefault();
    stableRouteSystem.dismountMidRoute();
  } else if (e.key.toLowerCase() === 'h') {
    if (housingModeController.isHousingActive()) {
      housingModeController.exitHousingMode();
    } else {
      housingModeController.enterHousingMode();
    }
  } else if (e.key.toLowerCase() === 'i') {
    charSheetHud.openTab('inventory_20');
  } else if (e.key.toLowerCase() === 'g') {
    guildWindow.toggle();
  }
});

// 6. Top Navigation Bar (Class Selector & Formation Toggle)
let topNavEl: HTMLElement | null = null;
let topNavClockEl: HTMLElement | null = null;

function createTopNavBar(): void {
  const bar = document.createElement('div');
  bar.id = 'top-nav-bar';
  bar.style.display = 'none';
  bar.style.userSelect = 'none';
  bar.style.overflow = 'hidden';
  document.body.appendChild(bar);
  topNavEl = bar;
  HudLayout.wrap(bar, { id: 'menu', mode: 'free', anchor: 'tl', dx: 10, dy: 10, w: 480, h: 58, minW: 300, minH: 50 });

  const clockEl = document.createElement('div');
  clockEl.id = 'hud-clock';
  clockEl.style.display = 'none';
  clockEl.style.userSelect = 'none';
  document.body.appendChild(clockEl);
  topNavClockEl = clockEl;
  HudLayout.wrap(clockEl, { id: 'clock', mode: 'free', anchor: 'tr', dx: 10, dy: 166, w: 210, h: 62, minW: 150, minH: 44 });
  updateTopNavBar();
  if (typeof charSheetHud !== 'undefined' && charSheetHud) {
    charSheetHud.refreshIfOpen();
  }
}

function updateTopNavBar(): void {
  if (!topNavEl) return;
  const currentLeader = registry.getPlayer();
  const party = registry.getParty();
  const isCorridor = combatModule.isCorridorFormation();
  const env = renderer.getEnvironment();
  const curTime = env.getTimeOfDay();
  const curWeather = env.getWeather();
  const curSeason = env.getSeason();
  const activeLeader = currentLeader || player;
  const biomeInfo = getBiomeInfo(activeLeader.x, activeLeader.y);
  const clock = WorldClock.now();

  const timeConfigs: Record<string, { label: string; bg: string; border: string }> = {
    day: { label: '☀️ 한낮', bg: '#0369a1', border: '#38bdf8' },
    dusk: { label: '🌅 노을', bg: '#9a3412', border: '#f97316' },
    night: { label: '🌙 한밤', bg: '#312e81', border: '#818cf8' },
    dawn: { label: '🌄 여명', bg: '#581c87', border: '#c084fc' },
  };

  const weatherConfigs: Record<string, { label: string; bg: string; border: string }> = {
    clear: { label: '🌤️ 맑음', bg: '#1e293b', border: '#64748b' },
    rain: { label: '🌧️ 비', bg: '#0f4c81', border: '#38bdf8' },
    thunderstorm: { label: '⛈️ 뇌우', bg: '#3b0764', border: '#d8b4fe' },
    snow: { label: '❄️ 눈', bg: '#0e7490', border: '#67e8f9' },
    fog: { label: '🌫️ 안개', bg: '#334155', border: '#94a3b8' },
  };

  const seasonConfigs: Record<string, { label: string; bg: string; border: string }> = {
    spring: { label: '🌸 봄', bg: '#831843', border: '#f472b6' },
    summer: { label: '🌿 여름', bg: '#064e3b', border: '#34d399' },
    autumn: { label: '🍁 가을', bg: '#78350f', border: '#f59e0b' },
    winter: { label: '⛄ 겨울', bg: '#164e63', border: '#38bdf8' },
  };

  const tConf = timeConfigs[curTime] || timeConfigs.day;
  const wConf = weatherConfigs[curWeather] || weatherConfigs.clear;
  const sConf = seasonConfigs[curSeason] || seasonConfigs.spring;

  const isAdminLeader =
    (activeLeader.job || '').toLowerCase().includes('관리자') ||
    (activeLeader.job || '').toLowerCase().includes('admin') ||
    activeLeader.role === 'admin' ||
    !!(activeLeader as any).isAdmin;
  const mb = (id: string, icon: string, label: string, key: string): string =>
    `<button id="${id}" class="daoc-mbtn"><b>${icon}</b>${label}<i>${key || '&nbsp;'}</i></button>`;
  const kc = (a: 'character' | 'inventory' | 'party' | 'guild' | 'craft' | 'map'): string => displayKey(Settings.getBinding(a));
  const menuHtml = `
    <div class="daoc-menu-grid">
      ${mb('btn-open-charsheet', '🧍', '캐릭터', kc('character'))}
      ${mb('btn-open-inventory', '🎒', '인벤토리', kc('inventory'))}
      ${mb('btn-open-housing', '🏰', '하우징', 'H')}
      ${mb('btn-open-crafting', '⚒️', '제작/인챈트', kc('craft'))}
      ${mb('btn-open-worldmap', '🗺️', '지도', kc('map'))}
      ${mb('btn-open-party', '👥', '파티', kc('party'))}
      ${mb('btn-open-guild', '🏰', '길드', kc('guild'))}
      ${mb('btn-open-shop-inworld', '💎', '다이아상점', '')}
      ${mb('btn-open-options', '⚙️', '옵션', 'ESC')}
      <button id="btn-open-gm" class="daoc-mbtn daoc-gm"><b>👑</b>GM<i>F12</i></button>
    </div>`;
  const badge = (id: string, conf: { label: string; bg: string; border: string }, label?: string): string =>
    `<span id="${id}" style="background:${conf.bg};border:1px solid ${conf.border};color:#fff;padding:1px 6px;border-radius:3px;font-size:10px;font-weight:bold;">${label || conf.label}</span>`;
  const clockHtml = `
    <div style="padding:5px 8px;text-align:center;">
      <div id="nav-world-clock-text" style="font-size:12px;color:#fde047;font-weight:800;letter-spacing:.3px;">⏰ ${clock.formattedTime}</div>
      <div style="display:flex;gap:4px;justify-content:center;margin-top:4px;">
        ${badge('nav-world-time-badge', tConf)}${badge('nav-world-season-badge', sConf)}${badge('nav-world-weather-badge', wConf, clock.weatherLabel)}
      </div>
    </div>`;

  topNavEl.innerHTML = menuHtml;
  if (topNavClockEl) topNavClockEl.innerHTML = clockHtml;

  // Environment buttons listeners
  const timeBtn = topNavEl.querySelector('#btn-toggle-time');
  if (timeBtn) {
    timeBtn.addEventListener('click', () => {
      renderer.getEnvironment().cycleTimeOfDay();
      updateTopNavBar();
    });
  }

  const weatherBtn = topNavEl.querySelector('#btn-toggle-weather');
  if (weatherBtn) {
    weatherBtn.addEventListener('click', () => {
      renderer.getEnvironment().cycleWeather();
      updateTopNavBar();
    });
  }

  const seasonBtn = topNavEl.querySelector('#btn-toggle-season');
  if (seasonBtn) {
    seasonBtn.addEventListener('click', () => {
      const p = registry.getPlayer() || player;
      renderer.getEnvironment().cycleSeason(p.x, p.y);
      updateTopNavBar();
    });
  }

  // Sound toggle button listener
  const soundBtn = topNavEl.querySelector('#btn-toggle-sound');
  if (soundBtn) {
    soundBtn.addEventListener('click', () => {
      soundManager.toggleMute();
      updateTopNavBar();
    });
  }

  // Zoom buttons listeners
  const zoomInBtn = topNavEl.querySelector('#btn-zoom-in');
  if (zoomInBtn) {
    zoomInBtn.addEventListener('click', () => {
      renderer.setTargetZoom(renderer.getZoom() + 0.2);
    });
  }
  const zoomOutBtn = topNavEl.querySelector('#btn-zoom-out');
  if (zoomOutBtn) {
    zoomOutBtn.addEventListener('click', () => {
      renderer.setTargetZoom(renderer.getZoom() - 0.2);
    });
  }
  const zoomResetBtn = topNavEl.querySelector('#btn-zoom-reset');
  if (zoomResetBtn) {
    zoomResetBtn.addEventListener('click', () => {
      renderer.setTargetZoom(1.0);
    });
  }

  // Click listeners for class switch buttons
  const heroBtns = topNavEl.querySelectorAll('[data-hero-id]');
  heroBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const heroId = btn.getAttribute('data-hero-id');
      if (heroId) switchPlayerCharacter(heroId);
    });
  });

  const formationBtn = topNavEl.querySelector('#btn-toggle-formation');
  if (formationBtn) {
    formationBtn.addEventListener('click', () => {
      combatModule.toggleCorridorFormation();
      updateTopNavBar();
    });
  }

  // Stable-to-Stable Automated Horse Route Quick Buttons
  topNavEl.querySelectorAll('[data-stable-route]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const pair = (btn.getAttribute('data-stable-route') || '').split(',');
      if (pair.length === 2) {
        const p = registry.getPlayer() || player;
        const nearest = stableRouteSystem.getNearestStableInCurrentZone(p.x, p.y);
        const fromId = nearest ? nearest.id : pair[0];
        const toId = fromId === pair[1] ? pair[0] : pair[1];
        stableRouteSystem.startRoute(fromId, toId);
      }
    });
  });

  topNavEl.querySelector('#btn-horse-dismount-top')?.addEventListener('click', () => {
    stableRouteSystem.dismountMidRoute();
  });

  topNavEl.querySelector('#btn-horse-board')?.addEventListener('click', () => {
    charSheetHud.openTab('world_map');
  });

  topNavEl.querySelector('#btn-zoom-fit')?.addEventListener('click', () => {
    renderer.setTargetZoom(renderer.getZoom() < 0.35 ? 1.0 : 0.16);
  });

  // 1-Click Zone Switcher Buttons in Top Bar
  topNavEl.querySelectorAll('[data-quick-zone]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const zId = parseInt(btn.getAttribute('data-quick-zone') || '1', 10);
      teleportPartyToZone(zId, 80, 80);
    });
  });

  topNavEl.querySelector('#btn-open-custom')?.addEventListener('click', () => {
    charSheetHud.openTab('race_stat');
  });
  topNavEl.querySelector('#btn-open-party')?.addEventListener('click', () => {
    partyWindow.toggle();
  });
  topNavEl.querySelector('#btn-open-guild')?.addEventListener('click', () => {
    guildWindow.toggle();
  });
  topNavEl.querySelector('#btn-open-inventory')?.addEventListener('click', () => {
    charSheetHud.openTab('inventory_20');
  });
  topNavEl.querySelector('#btn-open-charsheet')?.addEventListener('click', () => {
    charSheetHud.openTab('spec_skill');
  });
  topNavEl.querySelector('#btn-open-housing')?.addEventListener('click', () => {
    housingModeController.enterHousingMode();
  });
  topNavEl.querySelector('#btn-open-crafting')?.addEventListener('click', () => {
    charSheetHud.openTab('craft_enchant');
  });
  topNavEl.querySelector('#btn-open-worldmap')?.addEventListener('click', () => {
    charSheetHud.openTab('world_map');
  });
  topNavEl.querySelector('#btn-open-shop-inworld')?.addEventListener('click', () => {
    royalShopModal.open();
  });
  topNavEl.querySelector('#btn-open-gm')?.addEventListener('click', () => {
    adminConsole.toggle();
  });
  topNavEl.querySelector('#btn-open-options')?.addEventListener('click', () => {
    openEscMenu();
  });
}

// Mount Top Navigation Bar and initial HUD state
createTopNavBar();

// Initially hide world gameplay UI during SPLASH, MAIN_MENU, and CHAR_CREATE
setWorldUIActive(false);

// In-Game GM Brush Painting, Prop Placement & Demolition Intercept (Capturing Phase)
let isGmPainting = false;
canvas.addEventListener('mousedown', (e) => {
  if (e.button !== 0) return;
  const rect = canvas.getBoundingClientRect();
  const clickX = e.clientX - rect.left;
  const clickY = e.clientY - rect.top;
  const zoom = renderer.getZoom();
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const unzoomedX = (clickX - cx) / zoom + cx;
  const unzoomedY = (clickY - cy) / zoom + cy;
  const cam = renderer.getCameraOffset();
  const worldTile = screenToTile(unzoomedX, unzoomedY, cam.x, cam.y);

  // If Housing Mode is active, start drag-painting or erasing
  if (housingModeController.isHousingActive()) {
    housingModeController.isPainting = true;
    housingModeController.applyPaintAt(Math.round(worldTile.x), Math.round(worldTile.y));
    e.stopPropagation();
    e.stopImmediatePropagation();
    return;
  }

  if (adminConsole.brushActive) {
    isGmPainting = true;
  }

  if (adminConsole.handleCanvasClick(worldTile.x, worldTile.y)) {
    e.stopPropagation();
    e.stopImmediatePropagation();
    return;
  }
}, true);

window.addEventListener('mouseup', () => {
  isGmPainting = false;
  if (housingModeController.isHousingActive()) {
    housingModeController.isPainting = false;
  }
});

canvas.addEventListener('mousemove', (e) => {
  if (housingModeController.isHousingActive()) {
    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const zoom = renderer.getZoom();
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const unzoomedX = (clickX - cx) / zoom + cx;
    const unzoomedY = (clickY - cy) / zoom + cy;
    const cam = renderer.getCameraOffset();
    const worldTile = screenToTile(unzoomedX, unzoomedY, cam.x, cam.y);
    
    // Always update cursor position for live ghost preview
    housingModeController.cursorX = Math.round(worldTile.x);
    housingModeController.cursorY = Math.round(worldTile.y);

    if (housingModeController.isPainting) {
      housingModeController.applyPaintAt(housingModeController.cursorX, housingModeController.cursorY);
    }
    return;
  }

  if (!isGmPainting || !adminConsole.brushActive) return;
  const rect = canvas.getBoundingClientRect();
  const clickX = e.clientX - rect.left;
  const clickY = e.clientY - rect.top;
  const zoom = renderer.getZoom();
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const unzoomedX = (clickX - cx) / zoom + cx;
  const unzoomedY = (clickY - cy) / zoom + cy;
  const cam = renderer.getCameraOffset();
  const worldTile = screenToTile(unzoomedX, unzoomedY, cam.x, cam.y);
  adminConsole.handleCanvasClick(worldTile.x, worldTile.y);
});

// Click-to-Teleport on Canvas Road Gateways & Click NPC to Open 20-Slot Inventory
canvas.addEventListener('mousedown', (e) => {
  if (e.button !== 0) return;
  const rect = canvas.getBoundingClientRect();
  const clickX = e.clientX - rect.left;
  const clickY = e.clientY - rect.top;
  const zoom = renderer.getZoom();
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const unzoomedX = (clickX - cx) / zoom + cx;
  const unzoomedY = (clickY - cy) / zoom + cy;
  const cam = renderer.getCameraOffset();
  const worldTile = screenToTile(unzoomedX, unzoomedY, cam.x, cam.y);

  // 0) If holding money on cursor, clicking an NPC, Companion or Target initiates DAoC Trade!
  if (moneySelectorModalInstance.hasMoneyInHand()) {
    for (const ent of registry.getAll()) {
      if (ent.id !== player.id && Math.hypot(worldTile.x - ent.x, worldTile.y - ent.y) < 2.0) {
        moneySelectorModalInstance.handleTargetClick(ent, player);
        return;
      }
    }
  }

  // 0.5) Check if clicked on Red Mailbox
  const placedList = housingSystem.getPlacedObjectsInZone(renderer.getActiveZoneId());
  for (const obj of placedList) {
    if (obj.catalogId === 'facil_red_mailbox' && Math.hypot(worldTile.x - obj.x, worldTile.y - obj.y) < 1.4) {
      mailboxModalInstance.open(renderer.getActiveZoneId(), obj.x, obj.y, player);
      return;
    }
  }

  // 1) Check if clicked on any World NPC
  for (const ent of registry.getAll()) {
    if (ent.category === 'npc' && Math.hypot(worldTile.x - ent.x, worldTile.y - ent.y) < 1.8) {
      const isEnchanter = Boolean(
        ent.id.includes('enchant') ||
        (ent.job && ent.job.includes('인챈터')) ||
        (ent.name && ent.name.includes('인챈터'))
      );
      const isStable = Boolean(
        ent.id.includes('stable') ||
        (ent.job && ent.job.includes('마구간')) ||
        (ent.name && ent.name.includes('마구간'))
      );

      if (isEnchanter) {
        enchanterModalInstance.open(player, ent);
      } else if (isStable) {
        npcShopModalInstance.open(player, ent, registry.getParty().length);
      } else {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `💬 [${ent.name}]: "저희는 물건을 팔지 않습니다. 모든 물건은 직접 제작하거나 유저들과 서로 거래(교환)하세요!"`,
          category: 'system',
        });
      }
      return;
    }
  }

  // 2) Check if clicked on any Stable Node in the current Zone -> Open Horse Merchant Shop!
  const curZoneStables = STABLE_NODES.filter((s) => s.zoneId === renderer.getActiveZoneId());
  for (const stNode of curZoneStables) {
    if (Math.hypot(worldTile.x - stNode.x, worldTile.y - stNode.y) < 2.6) {
      const jackson = registry.get('npc_stable_jackson');
      if (jackson) {
        npcShopModalInstance.open(player, jackson, registry.getParty().length);
      }
      return;
    }
  }
  const sm = renderer.getActiveZoneData().stableMaster;
  if (Math.hypot(worldTile.x - sm.x, worldTile.y - sm.y) < 2.2) {
    const jackson = registry.get('npc_stable_jackson');
    if (jackson) {
      npcShopModalInstance.open(player, jackson, registry.getParty().length);
    }
    return;
  }

  // 3) Check if clicked on any of the directional Road Gateways / Portals
  for (const gw of renderer.getZoneGateways()) {
    const inBox = gw.startX !== undefined && (worldTile.x >= gw.startX - 1.0 && worldTile.x <= gw.endX + 1.0 &&
                   worldTile.y >= gw.startY - 1.0 && worldTile.y <= gw.endY + 1.0);
    const dist = Math.hypot(worldTile.x - gw.x, worldTile.y - gw.y);
    if (inBox || dist < 2.6) {
      teleportPartyToZone(gw.targetZoneId, gw.spawnX, gw.spawnY);
      return;
    }
  }
});

EventBus.on('COMBAT_STATE_CHANGED', () => {
  updateTopNavBar();
  if (typeof charSheetHud !== 'undefined' && charSheetHud) {
    charSheetHud.refreshIfOpen();
  }
});

// 7. Game Loop Start (Dynamically tracks active player & Stable-to-Stable Horse Route)
let bannerUpdateAccum = 0;
let clockSyncAccum = 0;

function updateTopNavWorldClock(): void {
  const clockEl = document.getElementById('nav-world-clock-text');
  if (!clockEl) return;
  const clock = WorldClock.now();
  clockEl.textContent = `⏰ ${clock.formattedTime}`;

  const timeBadge = document.getElementById('nav-world-time-badge');
  if (timeBadge) {
    const timeConfigs: Record<string, { label: string; bg: string; border: string }> = {
      day: { label: '☀️ 한낮', bg: '#0369a1', border: '#38bdf8' },
      dusk: { label: '🌅 노을', bg: '#9a3412', border: '#f97316' },
      night: { label: '🌙 한밤', bg: '#312e81', border: '#818cf8' },
      dawn: { label: '🌄 여명', bg: '#581c87', border: '#c084fc' },
    };
    const tConf = timeConfigs[clock.timeOfDay] || timeConfigs.day;
    timeBadge.textContent = tConf.label;
    timeBadge.style.background = tConf.bg;
    timeBadge.style.borderColor = tConf.border;
  }

  const seasonBadge = document.getElementById('nav-world-season-badge');
  if (seasonBadge) {
    const seasonConfigs: Record<string, { label: string; bg: string; border: string }> = {
      spring: { label: '🌸 봄', bg: '#831843', border: '#f472b6' },
      summer: { label: '🌿 여름', bg: '#064e3b', border: '#34d399' },
      autumn: { label: '🍁 가을', bg: '#78350f', border: '#f59e0b' },
      winter: { label: '⛄ 겨울', bg: '#164e63', border: '#38bdf8' },
    };
    const sConf = seasonConfigs[clock.season] || seasonConfigs.spring;
    seasonBadge.textContent = sConf.label;
    seasonBadge.style.background = sConf.bg;
    seasonBadge.style.borderColor = sConf.border;
  }

  const weatherBadge = document.getElementById('nav-world-weather-badge');
  if (weatherBadge) {
    const weatherConfigs: Record<string, { label: string; bg: string; border: string }> = {
      clear: { label: '🌤️ 맑음', bg: '#1e293b', border: '#64748b' },
      rain: { label: '🌧️ 비', bg: '#0f4c81', border: '#38bdf8' },
      thunderstorm: { label: '⛈️ 뇌우', bg: '#3b0764', border: '#d8b4fe' },
      snow: { label: '❄️ 눈', bg: '#0e7490', border: '#67e8f9' },
      fog: { label: '🌫️ 안개', bg: '#334155', border: '#94a3b8' },
    };
    const wConf = weatherConfigs[clock.weather] || weatherConfigs.clear;
    weatherBadge.textContent = clock.weatherLabel || wConf.label;
    weatherBadge.style.background = wConf.bg;
    weatherBadge.style.borderColor = wConf.border;
  }
}

const loop = new GameLoop(
  (dt) => {
    // 월드에 진입한 상태(IN_WORLD)가 아닐 때는(메인 메뉴, 캐릭터 생성, 캐릭터 선택, 상점, 설정 등) 월드 게임 로직 및 렌더링 갱신 차단!
    if (gameState !== 'IN_WORLD') {
      return;
    }

    const activePlayer = registry.getPlayer() || player;
    adminConsole.update(dt);

    // 0. 실시간 월드 클럭 동기화 (0.25초마다 인게임 시계 갱신: 1초에 30초 흐름)
    clockSyncAccum += dt;
    if (clockSyncAccum >= 0.25) {
      clockSyncAccum = 0;
      updateTopNavWorldClock();
    }

    const activeZoneData = renderer.getActiveZoneData();
    if (stableRouteSystem.isRiding()) {
      stableRouteSystem.update(dt);
      const st = stableRouteSystem.getRouteStatus();
      renderer.setActiveHorseRouteWaypoints(st.remainingWaypoints);
      bannerUpdateAccum += dt;
      if (bannerUpdateAccum >= 0.15) {
        bannerUpdateAccum = 0;
        updateStableRideBanner();
      }
    } else {
      renderer.setActiveHorseRouteWaypoints([]);
      input.updateMovement(activePlayer, dt, activeZoneData);
    }

    combatModule.update(dt, activeZoneData);
    gatheringModule.update(dt);
    partyHud.update(activePlayer, registry);
    partyWindow.update(activePlayer, dt);
    net.tick(dt, activePlayer, renderer.getActiveZoneId());
    remotePlayers.update(dt);
    groupHud.update(
      partyManager.getMemberViews().map((m) => {
        let distText = '';
        if (m.isMe) {
          distText = '나';
        } else {
          // Check remote players for distance
          const rem = remotePlayers.list.find((r) => r.name === m.name);
          if (rem) {
            const d = Math.round(Math.hypot(activePlayer.x - rem.x, activePlayer.y - rem.y));
            distText = `${d}m`;
          } else {
            distText = '원거리';
          }
        }
        return { name: m.name, hp: m.hp, leader: m.leader, distText };
      }),
      partyManager.isInParty()
    );
    targetFrame.update(activePlayer, registry, combatModule.isStick());
    actionBar.update(activePlayer);

    // Check Automatic Walking Zone Boundary Transition via Road Gateways (when not on automated multi-zone horse route)
    if (!stableRouteSystem.isRiding()) {
      if (zoneTransitionCooldown > 0) {
        zoneTransitionCooldown -= dt;
      } else {
        for (const gw of renderer.getZoneGateways()) {
          const inBox = gw.startX !== undefined && (activePlayer.x >= gw.startX - 0.75 && activePlayer.x <= gw.endX + 0.75 &&
                         activePlayer.y >= gw.startY - 0.75 && activePlayer.y <= gw.endY + 0.75);
          const dist = Math.hypot(activePlayer.x - gw.x, activePlayer.y - gw.y);
          if (inBox || dist <= 1.85) {
            teleportPartyToZone(gw.targetZoneId, gw.spawnX, gw.spawnY);
            break;
          }
        }
      }
    }

    // 8. Update Ambient BGM & Biome Boundary Transitions (Only when playing in-world)
    const env = renderer.getEnvironment();
    env.checkBiomeTransition(activePlayer.x, activePlayer.y);
    soundManager.updateAmbient(
      dt,
      activePlayer.x,
      activePlayer.y,
      env.getSeason(),
      env.getTimeOfDay(),
      env.getWeather(),
      gameState === 'IN_WORLD'
    );
  },
  (interp) => {
    if (gameState !== 'IN_WORLD') {
      return;
    }
    renderer.render(1 / 60);
  }
);

loop.start();
splashScreen.notifyEngineLoaded();
console.log('⚔️ [Terra Anima] DAoC Tab-Combat Engine with 6-Class Switch Initialized!');

// 클라이언트 버전 검사 초기화 (업데이트 필요 시 캐릭터 저장 후 메인메뉴 복귀)
VersionChecker.init(() => {
  if (gameState === 'IN_WORLD') {
    saveCurrentCharacter();
    currentSlotId = null;
    net.disconnect();
    gameState = 'MAIN_MENU';
    setWorldUIActive(false);
    mainMenu.show();
  }
});
